import { FC, ReactNode, useMemo, useState } from 'react';
import { WithScope } from '@mews-commander/types';
import { FeatureFlag, SearchType } from '@mews-commander/enums';
import { createZodFormResolver, lt, useFeatureFlag, useFormControls } from '@mews/framework';
import { useCommanderSelector, useGainsightCommanderEvents } from '@mews-commander/utils';
import { isCurrentProfileEnabledToManageCustomers } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { getReservationsCustomerIds } from '@mews-commander/legacy-entities/src/ducks/reservations';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { FormLayout } from '../../FormLayout';
import { SearchHookField } from '../../../../../components/form/hookFields';
import { useRoutingRuleChangesConfirmation } from '../../../../routingRulesNew/components/RoutingRuleChangeConfirmationPanel/useRoutingRuleChangesConfirmation';
import { BookerFormKeys, bookerFormSchema, BookerFormValues } from './types';
import { getBookerFormInitialValues } from './selectors';
import { useUpdateBookerMutation } from './useUpdateBookerMutation';

interface BookerFormProps extends WithScope {
    reservationIds: string[];
    onSuccess: () => void;
}

export const BookerForm: FC<BookerFormProps> = ({ scope, reservationIds, onSuccess }: BookerFormProps) => {
    const defaultValues = useCommanderSelector((state) => getBookerFormInitialValues(state, { scope }));
    const canManageCustomers = useCommanderSelector((state) => isCurrentProfileEnabledToManageCustomers(state));
    const mutation = useUpdateBookerMutation(reservationIds);
    const [routingRuleChangesDialog, setRoutingRuleChangesDialog] = useState<ReactNode>(null);

    const handleSubmit = (values: BookerFormValues) => {
        const result = validate();

        if (result) {
            setRoutingRuleChangesDialog(result.dialog);
            return;
        }

        mutation.mutate(values, { onSuccess });
    };

    const { renderFormContent, reset, watch, hasDirtyFields } = useFormControls({
        onSubmit: handleSubmit,
        resolver: createZodFormResolver(bookerFormSchema),
        values: defaultValues,
        dataTestForm: 'booker-form',
    });

    const enterpriseId = useCurrentEnterpriseId();

    const accountIds = useCommanderSelector((state) =>
        getReservationsCustomerIds(state, { ids: reservationIds, enterpriseId })
    );

    const gainsightEvents = useGainsightCommanderEvents();

    const granularRoutingRulesConditionsExtensionEnabled = useFeatureFlag(
        FeatureFlag.granularRoutingRulesConditionsExtensionEnabled
    );

    const bookerId = watch(BookerFormKeys.customerId);
    const updatedAccountIds = useMemo(() => {
        if (bookerId !== defaultValues[BookerFormKeys.customerId]) {
            if (bookerId === null) {
                return accountIds.filter((id) => id !== defaultValues[BookerFormKeys.customerId]);
            }
            if (bookerId && !accountIds.includes(bookerId)) {
                return [...accountIds.filter((id) => id !== defaultValues[BookerFormKeys.customerId]), bookerId];
            }
        }

        return accountIds;
    }, [accountIds, bookerId, defaultValues]);

    const { validate, isLoading } = useRoutingRuleChangesConfirmation({
        reservationIds,
        accountIds: updatedAccountIds,
        enabled: granularRoutingRulesConditionsExtensionEnabled && hasDirtyFields,
        scope,
        onSubmit: ({ changes }) => {
            const onInternalSuccess = () => {
                gainsightEvents.onApiSuccess({
                    context: 'BillingAutomationChanged',
                    data: {
                        changes,
                        origin: 'Account',
                    },
                });

                onSuccess?.();
            };

            mutation.mutate(watch(), { onSuccess: onInternalSuccess });
            setRoutingRuleChangesDialog(null);
        },
        onCancel: () => {
            reset();
            setRoutingRuleChangesDialog(null);
        },
    });

    return (
        <>
            {renderFormContent(
                <FormLayout loading={mutation.isPending} submitButtonLoading={isLoading} vertical={false}>
                    <SearchHookField
                        data-test-id={BookerFormKeys.customerId}
                        newEntity={canManageCustomers}
                        name={BookerFormKeys.customerId}
                        label={lt('Booker')}
                        searchTypes={[SearchType.Customer]}
                    />
                </FormLayout>
            )}
            {routingRuleChangesDialog}
        </>
    );
};
